import { DEFAULT_OFFICE_ADDRESS_KEY, isValidOfficeAddressKey } from "@/data/office-addresses";
import { ExtendedUserWithProfile, LanguageType, ThemeType } from "@/lib/types";
import { signOut } from "next-auth/react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

// Helper functions for language management
const setLanguageCookie = (language: LanguageType) => {
  if (typeof document !== "undefined") {
    document.cookie = `language=${language}; path=/; max-age=31536000; Secure; SameSite=Strict`;
  }
};

const getLanguageFromCookie = (): LanguageType => {
  if (typeof document !== "undefined") {
    const cookie = document.cookie.split("; ").find((row) => row.startsWith("language="));
    return cookie ? (cookie.split("=")[1] as LanguageType) : LanguageType.EN;
  }
  return LanguageType.EN; // Default to English if cookie is not available
};

// AppSettings State Interface
interface AppSettingsState {
  drawerOpen: boolean;
  sidebarOpenDesktop: boolean;
  sidebarOpenMobile: boolean;
  hydrated: boolean;
  isLoading: boolean;
  language: LanguageType;
  user: ExtendedUserWithProfile | null;
  consentModalOpen: boolean;
  theme: string; // 'light' or 'dark'
  businessCardAddressKey: string;
  setBusinessCardAddressKey: (key: string) => void;
  setTheme: (theme: string) => void;
  toggleTheme: () => void;
  setSidebarOpenMobile: (isOpen: boolean) => void;
  setSidebarOpenDesktop: (isOpen: boolean) => void;
  setDrawerOpen: (isOpen: boolean) => void;
  setHydrated: (hydrated: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setLanguage: (language: LanguageType) => void;
  setUser: (user: ExtendedUserWithProfile | null) => void;
  clearUser: () => void;
  setConsentModalOpen: (isOpen: boolean) => void;
}

// AppSettings Zustand store
const useAppSettings = create<AppSettingsState>()(
  persist(
    (set, get) => ({
      drawerOpen: false,
      setDrawerOpen: (isOpen: boolean) => set({ drawerOpen: isOpen }),
      sidebarOpenDesktop: true,
      setSidebarOpenDesktop: (isOpen: boolean) => set({ sidebarOpenDesktop: isOpen }),
      sidebarOpenMobile: false,
      setSidebarOpenMobile: (isOpen: boolean) => set({ sidebarOpenMobile: isOpen }),
      hydrated: false,
      setHydrated: (hydrated: boolean) => set({ hydrated }),
      isLoading: false,
      setIsLoading: (isLoading: boolean) => set({ isLoading }),
      language: getLanguageFromCookie(),
      setLanguage: (language: LanguageType) => {
        set({ language });
        setLanguageCookie(language); // Update cookie for persistence
      },
      user: null,
      setUser: (user) => set({ user }),
      clearUser: () => {
        set({ user: null });
        signOut({ redirect: false });
      },
      consentModalOpen: false,
      setConsentModalOpen: (isOpen: boolean) => set({ consentModalOpen: isOpen }),
      // Client only, never sent to the database (spec 0002 AC-3). A
      // persisted key no longer in the static list self heals to the
      // default rather than being sent to the server.
      businessCardAddressKey: DEFAULT_OFFICE_ADDRESS_KEY,
      setBusinessCardAddressKey: (key: string) =>
        set({
          businessCardAddressKey: isValidOfficeAddressKey(key)
            ? key
            : DEFAULT_OFFICE_ADDRESS_KEY,
        }),
      theme: ThemeType.LIGHT, // Default theme
      setTheme: (theme: string) => set({ theme }),
      toggleTheme: () => {
        const newTheme = get().theme === ThemeType.LIGHT ? ThemeType.DARK : ThemeType.LIGHT;
        get().setTheme(newTheme);
      },
    }),
    {
      name: "app-settings",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sidebarOpenDesktop: state.sidebarOpenDesktop,
        language: state.language,
        user: state.user,
        theme: state.theme,
        businessCardAddressKey: state.businessCardAddressKey,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // A key persisted from a since-shrunk static list self heals to
          // the default here too, not just on the next setter call.
          if (!isValidOfficeAddressKey(state.businessCardAddressKey)) {
            state.businessCardAddressKey = DEFAULT_OFFICE_ADDRESS_KEY;
          }
          state.setHydrated(true);
        }
      },
    }
  )
);

export default useAppSettings;